import React from 'react'
import { fetchAdminOverview } from '../../../lib/billingApi'
import { formatMoney } from '../../../lib/utils'
import { StatCard } from '../components/StatCard'
import { Skeleton } from '../../../components/ui/Skeleton'
import { BusinessDetailModal } from './BusinessDetailModal'
import type { AdminOverview } from '../../../types/api'

export function AdminOverviewPage() {
  const [data, setData] = React.useState<AdminOverview | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [refreshedAt, setRefreshedAt] = React.useState<Date | null>(null)
  const [detailId, setDetailId] = React.useState<string | null>(null)

  const load = React.useCallback(() => {
    setLoading(true)
    setError(null)
    fetchAdminOverview()
      .then(d => { setData(d); setRefreshedAt(new Date()) })
      .catch(() => setError('Failed to load overview.'))
      .finally(() => setLoading(false))
  }, [])

  React.useEffect(() => {
    load()
    const handle = window.setInterval(load, 60_000)
    return () => window.clearInterval(handle)
  }, [load])

  const kpis = data?.kpis

  // delta helpers
  const sentDelta = kpis
    ? kpis.sentPrev24h === 0 && kpis.sentLast24h === 0
      ? null
      : kpis.sentPrev24h === 0
        ? null
        : ((kpis.sentLast24h - kpis.sentPrev24h) / kpis.sentPrev24h) * 100
    : null

  const failRate = kpis && kpis.sentLast24h + kpis.failedLast24h > 0
    ? (kpis.failedLast24h / (kpis.sentLast24h + kpis.failedLast24h)) * 100
    : 0

  return (
    <div className="content" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--text)', letterSpacing: '-0.02em' }}>Admin Overview</div>
          <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 3 }}>
            System-wide health — auto-refreshes every minute
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {refreshedAt && (
            <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: "'DM Mono', monospace" }}>
              Updated {refreshedAt.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="panel-action"
            style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'wait' : 'pointer' }}
          >
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: 16, border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)' }}>
          <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 8 }}>{error}</div>
          <button className="btn-primary" onClick={load} style={{ padding: '4px 12px', fontSize: 12 }}>Retry</button>
        </div>
      )}

      {/* ── Row 1: User & Business stats ── */}
      <div className="stats-row">
        <StatCard
          className="sc-orange"
          delay=".05s"
          iconClass="ic-orange"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          }
          label="Total Users"
          value={loading ? <Skeleton height={30} width={56} style={{ marginBottom: 2 }} /> : (kpis?.usersTotal ?? 0).toLocaleString()}
          delta={
            loading ? <Skeleton height={12} width={80} /> : (
              <span style={{ color: 'var(--text3)' }}>
                <span style={{ color: 'var(--orange)', fontWeight: 600 }}>+{kpis?.usersNew7d ?? 0}</span>
                {' '}this week
              </span>
            )
          }
        />
        <StatCard
          className="sc-blue"
          delay=".1s"
          iconClass="ic-blue"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="7" width="20" height="14" rx="2" />
              <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
            </svg>
          }
          label="Businesses"
          value={loading ? <Skeleton height={30} width={56} style={{ marginBottom: 2 }} /> : (kpis?.businessesTotal ?? 0).toLocaleString()}
          delta={
            loading ? <Skeleton height={12} width={80} /> : (
              <span style={{ color: 'var(--text3)' }}>
                <span style={{ color: 'var(--blue)', fontWeight: 600 }}>+{kpis?.businessesNew7d ?? 0}</span>
                {' '}this week
              </span>
            )
          }
        />
        <StatCard
          className="sc-teal"
          delay=".15s"
          iconClass="ic-teal"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="1" y="4" width="22" height="16" rx="2" ry="2" /><line x1="1" y1="10" x2="23" y2="10" />
            </svg>
          }
          label="Active Subs"
          value={loading ? <Skeleton height={30} width={56} style={{ marginBottom: 2 }} /> : (kpis?.activeSubscriptions ?? 0).toLocaleString()}
          delta={
            loading ? <Skeleton height={12} width={80} /> : (
              <span style={{ color: 'var(--text3)' }}>
                <span style={{ color: 'var(--teal)', fontWeight: 600 }}>{kpis?.trialingSubscriptions ?? 0}</span>
                {' '}on trial
              </span>
            )
          }
        />
        <StatCard
          className="sc-green"
          delay=".2s"
          iconClass="ic-green"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
            </svg>
          }
          label="MRR"
          value={loading ? <Skeleton height={30} width={80} style={{ marginBottom: 2 }} /> : formatMoney(kpis?.mrrCents ?? 0, kpis?.currency ?? 'USD')}
          delta={
            loading ? <Skeleton height={12} width={80} /> : (
              <span style={{ color: 'var(--text3)' }}>Recurring revenue</span>
            )
          }
        />
      </div>

      {/* ── Row 2: Nudge pipeline stats ── */}
      <div className="stats-row" style={{ marginBottom: 0 }}>
        <StatCard
          className="sc-blue"
          delay=".05s"
          iconClass="ic-blue"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          }
          label="Sent 24h"
          value={loading ? <Skeleton height={30} width={56} style={{ marginBottom: 2 }} /> : (kpis?.sentLast24h ?? 0).toLocaleString()}
          delta={
            loading ? <Skeleton height={12} width={80} /> : (
              sentDelta === null
                ? <span style={{ color: 'var(--text3)' }}>vs prev 24h</span>
                : <span style={{ color: sentDelta >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 600 }}>
                    {sentDelta >= 0 ? '▲' : '▼'} {Math.abs(sentDelta).toFixed(0)}% vs prev
                  </span>
            )
          }
        />
        <StatCard
          className={kpis && kpis.failedLast24h > 0 ? 'sc-red' : 'sc-green'}
          delay=".1s"
          iconClass={kpis && kpis.failedLast24h > 0 ? 'ic-red' : 'ic-green'}
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          }
          label="Failed 24h"
          value={loading ? <Skeleton height={30} width={56} style={{ marginBottom: 2 }} /> : (kpis?.failedLast24h ?? 0).toLocaleString()}
          delta={
            loading ? <Skeleton height={12} width={80} /> : (
              <span style={{ color: kpis && kpis.failedLast24h > 0 ? 'var(--red)' : 'var(--text3)' }}>
                {failRate.toFixed(1)}% of attempts
              </span>
            )
          }
        />
        <StatCard
          className="sc-green"
          delay=".15s"
          iconClass="ic-green"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 11 12 14 22 4" />
              <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
            </svg>
          }
          label="Recovery 7d"
          value={loading ? <Skeleton height={30} width={56} style={{ marginBottom: 2 }} /> : `${((kpis?.recoveryRate7d ?? 0) * 100).toFixed(1)}%`}
          delta={
            loading ? <Skeleton height={12} width={80} /> : (
              <span style={{ color: 'var(--text3)' }}>
                <span style={{ color: 'var(--green)', fontWeight: 600 }}>{kpis?.recovered7d ?? 0}</span>
                {' '}/ {kpis?.sent7d ?? 0} sent
              </span>
            )
          }
        />
        <StatCard
          className={kpis && kpis.staleAiProcessing > 0 ? 'sc-red' : 'sc-teal'}
          delay=".2s"
          iconClass={kpis && kpis.staleAiProcessing > 0 ? 'ic-red' : 'ic-teal'}
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2a3 3 0 0 1 3 3v3" /><path d="M9 8V5a3 3 0 0 1 3-3" />
              <rect x="4" y="8" width="16" height="12" rx="3" /><circle cx="9" cy="14" r="1" /><circle cx="15" cy="14" r="1" />
            </svg>
          }
          label="AI Health"
          value={
            loading
              ? <Skeleton height={30} width={56} style={{ marginBottom: 2 }} />
              : kpis && kpis.staleAiProcessing > 0
                ? <span style={{ color: 'var(--red)' }}>{kpis.staleAiProcessing}</span>
                : '✓'
          }
          delta={
            loading ? <Skeleton height={12} width={80} /> : (
              kpis && kpis.staleAiProcessing > 0
                ? <span style={{ color: 'var(--red)' }}>stale AI rows</span>
                : <span style={{ color: 'var(--teal)' }}>Queue healthy</span>
            )
          }
        />
      </div>

      {/* ── Row 3: Chart + Top Businesses ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 20 }}>
        <NudgeChart days={data?.last7Days ?? []} loading={loading} />
        <TopBusinesses rows={data?.topBusinesses ?? []} loading={loading} onSelect={setDetailId} />
      </div>

      {/* ── Row 4: Activity feed ── */}
      <ActivityFeed events={data?.activity ?? []} loading={loading} onSelect={setDetailId} />

      {detailId && (
        <BusinessDetailModal businessId={detailId} onClose={() => setDetailId(null)} />
      )}
    </div>
  )
}

// ─── Chart ────────────────────────────────────────────────────────────────────

function NudgeChart({ days, loading }: { days: AdminOverview['last7Days']; loading: boolean }) {
  const max = Math.max(1, ...days.flatMap(d => [d.sent, d.recovered, d.failed]))
  const totalSent = days.reduce((s, d) => s + d.sent, 0)
  const totalRec = days.reduce((s, d) => s + d.recovered, 0)
  const totalFailed = days.reduce((s, d) => s + d.failed, 0)

  return (
    <div className="panel">
      <div className="panel-header">
        <div>
          <div className="panel-title">Nudge activity — last 7 days</div>
          <div className="panel-sub">
            {loading ? 'Loading…' : `${totalSent} sent · ${totalRec} recovered · ${totalFailed} failed`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 14, fontSize: 11, color: 'var(--text3)' }}>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, background: 'var(--accent)', borderRadius: 2, marginRight: 5 }} />Sent</span>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, background: 'var(--green)', borderRadius: 2, marginRight: 5 }} />Recovered</span>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, background: 'var(--red)', borderRadius: 2, marginRight: 5 }} />Failed</span>
        </div>
      </div>
      <div style={{ padding: '20px 20px 12px' }}>
        {loading ? (
          <div style={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>Loading chart…</div>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${days.length}, 1fr)`, gap: 8, alignItems: 'end', height: 140 }}>
            {days.map(d => (
              <div key={String(d.day)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 120, width: '100%', justifyContent: 'center' }}>
                  <div
                    style={{ width: 10, height: `${(d.sent / max) * 100}%`, background: 'var(--accent)', borderRadius: '3px 3px 0 0', minHeight: d.sent > 0 ? 2 : 0, opacity: 0.85 }}
                    title={`${d.sent} sent`}
                  />
                  <div
                    style={{ width: 10, height: `${(d.recovered / max) * 100}%`, background: 'var(--green)', borderRadius: '3px 3px 0 0', minHeight: d.recovered > 0 ? 2 : 0 }}
                    title={`${d.recovered} recovered`}
                  />
                  {d.failed > 0 && (
                    <div
                      style={{ width: 10, height: `${(d.failed / max) * 100}%`, background: 'var(--red)', borderRadius: '3px 3px 0 0', minHeight: 2, opacity: 0.7 }}
                      title={`${d.failed} failed`}
                    />
                  )}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: "'DM Mono', monospace" }}>
                  {new Date(String(d.day)).toLocaleDateString(undefined, { weekday: 'short' }).slice(0, 2)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Top businesses ───────────────────────────────────────────────────────────

function TopBusinesses({
  rows, loading, onSelect,
}: { rows: AdminOverview['topBusinesses']; loading: boolean; onSelect: (id: string) => void }) {
  return (
    <div className="panel">
      <div className="panel-header">
        <div>
          <div className="panel-title">Top businesses</div>
          <div className="panel-sub">By nudges sent in the last 7 days</div>
        </div>
      </div>
      <div style={{ padding: '4px 0 8px' }}>
        {loading ? (
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <div style={{ width: 20, height: 20, background: 'var(--surface2)', borderRadius: 4 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ height: 12, background: 'var(--surface2)', borderRadius: 4, marginBottom: 4, width: '60%' }} />
                  <div style={{ height: 10, background: 'var(--surface2)', borderRadius: 4, width: '40%' }} />
                </div>
                <div style={{ width: 24, height: 20, background: 'var(--surface2)', borderRadius: 4 }} />
              </div>
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '24px 20px', fontSize: 13, color: 'var(--text3)', textAlign: 'center' }}>No activity yet.</div>
        ) : (
          rows.map((r, i) => (
            <button
              key={r.businessId}
              onClick={() => onSelect(r.businessId)}
              style={{
                display: 'grid',
                gridTemplateColumns: '24px 1fr auto',
                gap: 12,
                alignItems: 'center',
                padding: '10px 20px',
                background: 'none',
                border: 'none',
                borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                cursor: 'pointer',
                textAlign: 'left',
                fontFamily: 'inherit',
                width: '100%',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface2)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: "'DM Mono', monospace", fontWeight: 600 }}>
                {String(i + 1).padStart(2, '0')}
              </span>
              <span style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.businessName}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, fontFamily: "'DM Mono', monospace" }}>
                  {r.planCode} · {r.contactCount.toLocaleString()} contacts
                </div>
              </span>
              <span style={{
                fontSize: 16, fontWeight: 600, color: r.sentLast7d > 0 ? 'var(--text)' : 'var(--text3)',
                fontFamily: "'DM Mono', monospace", minWidth: 24, textAlign: 'right',
              }}>
                {r.sentLast7d}
              </span>
            </button>
          ))
        )}
      </div>
    </div>
  )
}

// ─── Activity feed ────────────────────────────────────────────────────────────

const KIND_META: Record<AdminOverview['activity'][number]['kind'], { label: string; color: string; bg: string }> = {
  BUSINESS_SIGNUP:  { label: 'Signup',   color: 'var(--blue)',   bg: 'var(--blue-bg)'   },
  PLAN_CHANGE:      { label: 'Billing',  color: '#9b59b6',       bg: 'rgba(155,89,182,.12)' },
  NUDGE_FAILED:     { label: 'Failure',  color: 'var(--red)',    bg: 'var(--red-bg)'    },
  NUDGE_RECOVERED:  { label: 'Recovery', color: 'var(--green)',  bg: 'var(--accent-bg)' },
}

function relativeTime(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000
  if (diff < 60)    return `${Math.round(diff)}s ago`
  if (diff < 3600)  return `${Math.round(diff / 60)}m ago`
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`
  return `${Math.round(diff / 86400)}d ago`
}

function ActivityFeed({
  events, loading, onSelect,
}: { events: AdminOverview['activity']; loading: boolean; onSelect: (id: string) => void }) {
  return (
    <div className="panel">
      <div className="panel-header">
        <div>
          <div className="panel-title">Recent activity</div>
          <div className="panel-sub">Signups, plan changes, failures, and recoveries</div>
        </div>
        <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: "'DM Mono', monospace" }}>
          {!loading && `${events.length} events`}
        </span>
      </div>
      <div>
        {loading ? (
          <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[1, 2, 3, 4].map(i => (
              <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                <div style={{ width: 56, height: 20, background: 'var(--surface2)', borderRadius: 4 }} />
                <div style={{ flex: 1, height: 13, background: 'var(--surface2)', borderRadius: 4 }} />
                <div style={{ width: 48, height: 11, background: 'var(--surface2)', borderRadius: 4 }} />
              </div>
            ))}
          </div>
        ) : events.length === 0 ? (
          <div style={{ padding: '24px 20px', fontSize: 13, color: 'var(--text3)', textAlign: 'center' }}>No recent activity.</div>
        ) : (
          events.map((e, i) => {
            const meta = KIND_META[e.kind]
            return (
              <button
                key={`${e.at}-${e.businessId}-${i}`}
                onClick={() => onSelect(e.businessId)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '72px 1fr auto',
                  gap: 16,
                  alignItems: 'center',
                  padding: '11px 20px',
                  background: 'none',
                  border: 'none',
                  borderTop: i === 0 ? 'none' : '1px solid var(--border)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                  width: '100%',
                }}
                onMouseEnter={ev => (ev.currentTarget.style.background = 'var(--surface2)')}
                onMouseLeave={ev => (ev.currentTarget.style.background = 'transparent')}
              >
                <span style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
                  color: meta.color, background: meta.bg, padding: '3px 8px', borderRadius: 5,
                  textAlign: 'center', display: 'inline-block',
                }}>
                  {meta.label}
                </span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ fontSize: 13, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>
                    <strong style={{ fontWeight: 600 }}>{e.businessName}</strong>
                    <span style={{ color: 'var(--text2)', fontWeight: 400 }}> · {e.detail}</span>
                  </span>
                </span>
                <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: "'DM Mono', monospace", whiteSpace: 'nowrap' }}>
                  {e.at ? relativeTime(e.at) : '—'}
                </span>
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}
